import React, { useRef, useEffect, useMemo, Suspense, MutableRefObject, forwardRef, useImperativeHandle, useCallback } from 'react';
import { Canvas, useThree, useFrame } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import * as THREE from 'three';
import { STLLoader } from 'three/examples/jsm/loaders/STLLoader.js';
import { ColladaLoader } from 'three/examples/jsm/loaders/ColladaLoader.js';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader.js';
import URDFLoader from 'urdf-loader';
import type { URDFRobot } from 'urdf-loader';
import { Box, Typography } from '@mui/material';

export interface RobotViewer3DHandle {
  captureScreenshot: () => Promise<Blob | null>;
}

interface RobotViewer3DProps {
  urdfContent?: string;
  meshBlobUrls?: Map<string, string>;
  selectedJoint?: string;
  onJointClick?: (name: string) => void;
  jointValues?: Record<string, number>;
  showCollisionMeshes?: boolean;
  animatingJoint?: { name: string; lower: number; upper: number; axisFlipped?: boolean; continuous?: boolean } | null;
  focusJointName?: string | null;
  fullScreen?: boolean;
  panelOpenRight?: boolean;
  onMeshesLoaded?: () => void;
}

function resolveBlobUrl(
  path: string,
  meshBlobUrls: Map<string, string>,
): string | undefined {
  // Try the path as-is
  if (meshBlobUrls.has(path)) return meshBlobUrls.get(path);

  // Strip package:// or file:// prefixes
  const stripped = path.replace(/^(package|file):\/\/[^/]*\//, '');
  if (meshBlobUrls.has(stripped)) return meshBlobUrls.get(stripped);

  // Suffix match: URDF says "meshes/base.dae", key is "robot/meshes/base.dae"
  const suffix = '/' + stripped;
  const entries = Array.from(meshBlobUrls.entries());
  for (const [key, url] of entries) {
    if (key.endsWith(suffix) || key === stripped) return url;
  }

  // Basename only
  const base = path.split('/').pop() || '';
  for (const [key, url] of entries) {
    if (key.endsWith('/' + base) || key === base) return url;
  }

  return undefined;
}

function loadMeshFromBlobUrl(
  blobUrl: string,
  ext: string,
  meshBlobUrls: Map<string, string>,
  done: (obj: THREE.Object3D) => void,
  onLoaded?: () => void,
) {
  const fallbackMat = new THREE.MeshStandardMaterial({
    color: '#8899aa',
    metalness: 0.3,
    roughness: 0.6,
  });

  const finish = (obj: THREE.Object3D) => {
    done(obj);
    onLoaded?.();
  };

  if (ext === '.stl') {
    new STLLoader().load(blobUrl, (geometry) => {
      finish(new THREE.Mesh(geometry, fallbackMat));
    });
  } else if (ext === '.dae') {
    const manager = new THREE.LoadingManager();
    manager.setURLModifier((url) => {
      const resolved = resolveBlobUrl(url, meshBlobUrls);
      return resolved || url;
    });
    const loader = new ColladaLoader(manager);
    loader.load(blobUrl, (collada) => {
      // ColladaLoader auto-rotates Z-UP scenes by -90deg on X.
      // URDF handles orientation via joint transforms, so we need
      // to strip that rotation. Move children into a clean group.
      const scene = collada.scene;
      const wrapper = new THREE.Group();
      while (scene.children.length > 0) {
        wrapper.add(scene.children[0]);
      }
      finish(wrapper);
    });
  } else if (ext === '.obj') {
    new OBJLoader().load(blobUrl, (group) => {
      finish(group);
    });
  } else {
    const geo = new THREE.SphereGeometry(0.02, 12, 12);
    finish(new THREE.Mesh(geo, fallbackMat));
  }
}

function URDFModel({
  urdfContent,
  meshBlobUrls,
  selectedJoint,
  jointValues,
  robotRef,
  onMeshesLoaded,
}: {
  urdfContent: string;
  meshBlobUrls?: Map<string, string>;
  selectedJoint?: string;
  jointValues?: Record<string, number>;
  robotRef: MutableRefObject<URDFRobot | null>;
  onMeshesLoaded?: () => void;
}) {
  const groupRef = useRef<THREE.Group>(null);
  const { scene, invalidate } = useThree();
  const meshLoadRef = useRef({ total: 0, loaded: 0, fired: false });

  const hasMeshes = meshBlobUrls && meshBlobUrls.size > 0;

  const robot = useMemo(() => {
    meshLoadRef.current = { total: 0, loaded: 0, fired: false };

    try {
      if (hasMeshes) {
        // Count mesh references in URDF
        const meshCount = (urdfContent.match(/<mesh\s/g) || []).length;
        meshLoadRef.current.total = meshCount;

        // ZIP flow: render full visual meshes from blob URLs
        const loader = new URDFLoader();
        loader.packages = (_pkg: string) => '';
        loader.parseVisual = true;
        loader.parseCollision = false;
        loader.loadMeshCb = (path: string, _manager: THREE.LoadingManager, done: (obj: THREE.Object3D) => void) => {
          const blobUrl = resolveBlobUrl(path, meshBlobUrls!);
          if (!blobUrl) {
            const geo = new THREE.SphereGeometry(0.02, 12, 12);
            const mat = new THREE.MeshStandardMaterial({ color: '#cc6666', transparent: true, opacity: 0.5 });
            done(new THREE.Mesh(geo, mat));
            meshLoadRef.current.loaded++;
            if (meshLoadRef.current.loaded >= meshLoadRef.current.total && !meshLoadRef.current.fired) {
              meshLoadRef.current.fired = true;
              onMeshesLoaded?.();
            }
            return;
          }
          const ext = ('.' + path.split('.').pop()!).toLowerCase();
          loadMeshFromBlobUrl(blobUrl, ext, meshBlobUrls!, (obj) => {
            done(obj);
            meshLoadRef.current.loaded++;
            if (meshLoadRef.current.loaded >= meshLoadRef.current.total && !meshLoadRef.current.fired) {
              meshLoadRef.current.fired = true;
              onMeshesLoaded?.();
            }
          }, invalidate);
        };
        return loader.parse(urdfContent);
      }

      // Bare URDF flow: collision primitives fallback
      const loader = new URDFLoader();
      loader.packages = '';
      loader.parseVisual = false;
      loader.parseCollision = true;
      loader.loadMeshCb = (_path: string, _manager: THREE.LoadingManager, done: (obj: THREE.Object3D) => void) => {
        const geo = new THREE.SphereGeometry(0.02, 12, 12);
        const mat = new THREE.MeshStandardMaterial({ color: '#6699cc', transparent: true, opacity: 0.7 });
        done(new THREE.Mesh(geo, mat));
      };
      const parsed = loader.parse(urdfContent);

      // If collision geometry is empty, retry with visual
      let hasGeometry = false;
      parsed.traverse((child: THREE.Object3D) => {
        if ((child as THREE.Mesh).isMesh) hasGeometry = true;
      });
      if (!hasGeometry) {
        const fallback = new URDFLoader();
        fallback.packages = '';
        fallback.parseVisual = true;
        fallback.parseCollision = false;
        fallback.loadMeshCb = (_path: string, _manager: THREE.LoadingManager, done: (obj: THREE.Object3D) => void) => {
          const geo = new THREE.SphereGeometry(0.02, 12, 12);
          const mat = new THREE.MeshStandardMaterial({ color: '#6699cc', transparent: true, opacity: 0.7 });
          done(new THREE.Mesh(geo, mat));
        };
        return fallback.parse(urdfContent);
      }
      // No async mesh loading in fallback paths — fire immediately
      if (!meshLoadRef.current.fired) {
        meshLoadRef.current.fired = true;
        onMeshesLoaded?.();
      }
      return parsed;
    } catch (e) {
      console.error('URDF parse error:', e);
      return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urdfContent, hasMeshes]);

  useEffect(() => {
    if (!robot || !groupRef.current) return;
    robotRef.current = robot;

    // Clear previous children
    while (groupRef.current.children.length) {
      groupRef.current.remove(groupRef.current.children[0]);
    }
    groupRef.current.add(robot);

    return () => {
      // Dispose geometries and materials
      robot.traverse((child: any) => {
        if ((child as THREE.Mesh).geometry) {
          (child as THREE.Mesh).geometry.dispose();
        }
        if ((child as THREE.Mesh).material) {
          const mat = (child as THREE.Mesh).material;
          if (Array.isArray(mat)) {
            mat.forEach((m) => m.dispose());
          } else {
            mat.dispose();
          }
        }
      });
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [robot, scene]);

  // Apply joint values — when empty (reset), zero all joints
  useEffect(() => {
    if (!robotRef.current) return;
    let changed = false;
    if (jointValues && Object.keys(jointValues).length > 0) {
      Object.entries(jointValues).forEach(([name, value]) => {
        if (robotRef.current!.setJointValue(name, value)) changed = true;
      });
    } else {
      // Reset: set every joint to 0
      robotRef.current.traverse((child: THREE.Object3D) => {
        if ((child as any).isURDFJoint && (child as any).jointType !== 'fixed') {
          if (robotRef.current!.setJointValue((child as any).name, 0)) changed = true;
        }
      });
    }
    if (changed) invalidate();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jointValues, invalidate]);

  if (!robot) return null;
  // URDF is Z-up, Three.js is Y-up — rotate the whole assembly
  return <group ref={groupRef} rotation={[-Math.PI / 2, 0, 0]} />;
}

function JointHighlighter({
  robotRef,
  selectedJoint,
}: {
  robotRef: MutableRefObject<URDFRobot | null>;
  selectedJoint?: string;
}) {
  const highlightColor = useMemo(() => new THREE.Color('#EF8997'), []);
  const defaultEmissive = useMemo(() => new THREE.Color(0x000000), []);

  useFrame(({ clock }) => {
    if (!robotRef.current) return;
    const t = clock.getElapsedTime();
    const intensity = 0.3 + 0.15 * Math.sin(t * 3);

    Object.entries(robotRef.current.joints).forEach(([name, joint]) => {
      joint.traverse((child) => {
        if ((child as THREE.Mesh).isMesh) {
          const mat = (child as THREE.Mesh).material as THREE.MeshStandardMaterial;
          if (mat.emissive) {
            if (name === selectedJoint) {
              mat.emissive.copy(highlightColor);
              mat.emissiveIntensity = intensity;
            } else {
              mat.emissive.copy(defaultEmissive);
              mat.emissiveIntensity = 0;
            }
          }
        }
      });
    });
  });

  return null;
}

function JointAnimator({
  robotRef,
  animatingJoint,
}: {
  robotRef: MutableRefObject<URDFRobot | null>;
  animatingJoint: { name: string; lower: number; upper: number; axisFlipped?: boolean; continuous?: boolean };
}) {
  const startTimeRef = useRef<number | null>(null);
  const prevNameRef = useRef(animatingJoint.name);
  const origLimitsRef = useRef<Map<string, { lower: number; upper: number }>>(new Map());

  // Reset previous joint to 0 when the animated joint changes
  useEffect(() => {
    const prevName = prevNameRef.current;
    if (prevName !== animatingJoint.name && robotRef.current) {
      robotRef.current.setJointValue(prevName, 0);
      // Restore original limits on previous joint
      const orig = origLimitsRef.current.get(prevName);
      const prevJoint = robotRef.current.joints[prevName] as any;
      if (orig && prevJoint?.limit) {
        prevJoint.limit.lower = orig.lower;
        prevJoint.limit.upper = orig.upper;
      }
    }
    prevNameRef.current = animatingJoint.name;
    startTimeRef.current = null;
  }, [animatingJoint.name, robotRef]);

  useFrame(({ clock, invalidate }) => {
    if (!robotRef.current) return;
    const joint = robotRef.current.joints[animatingJoint.name] as any;
    if (!joint) return;

    // Save original limits the first time we touch this joint
    if (!origLimitsRef.current.has(animatingJoint.name) && joint.limit) {
      origLimitsRef.current.set(animatingJoint.name, {
        lower: joint.limit.lower,
        upper: joint.limit.upper,
      });
    }

    // Override joint limits so setJointValue doesn't clamp to stale URDF values
    const { lower, upper, axisFlipped, continuous } = animatingJoint;
    const effLower = axisFlipped ? -upper : lower;
    const effUpper = axisFlipped ? -lower : upper;

    if (continuous) {
      // Continuous: remove limits entirely so the joint can spin freely
      if (joint.limit) {
        joint.limit.lower = -Infinity;
        joint.limit.upper = Infinity;
      }
      // Also set jointType so urdf-loader doesn't clamp
      joint.jointType = 'continuous';
    } else if (joint.limit) {
      joint.limit.lower = effLower;
      joint.limit.upper = effUpper;
    }

    if (startTimeRef.current === null) {
      startTimeRef.current = clock.getElapsedTime();
    }
    const elapsed = clock.getElapsedTime() - startTimeRef.current;

    let value: number;
    if (continuous) {
      // Monotonic rotation — full turn every 3 seconds, like a tire
      const speed = (2 * Math.PI) / 3;
      value = axisFlipped ? -elapsed * speed : elapsed * speed;
    } else {
      value = effLower + ((Math.sin(elapsed * 2 * Math.PI / 3) + 1) / 2) * (effUpper - effLower);
    }

    robotRef.current.setJointValue(animatingJoint.name, value);
    invalidate();
  });

  return null;
}

function CameraFocuser({
  robotRef,
  focusJointName,
  controlsRef,
  panelOpenRight,
}: {
  robotRef: MutableRefObject<URDFRobot | null>;
  focusJointName: string;
  controlsRef: MutableRefObject<any>;
  panelOpenRight?: boolean;
}) {
  // Cache bounding box info — recompute only when robot identity changes
  const bboxRef = useRef<{ center: THREE.Vector3; radius: number } | null>(null);
  const bboxRobotId = useRef<URDFRobot | null>(null);
  const targetPos = useMemo(() => new THREE.Vector3(), []);
  const camGoal = useMemo(() => new THREE.Vector3(), []);

  useFrame(({ camera }) => {
    if (!robotRef.current || !controlsRef.current) return;
    const joint = robotRef.current.joints[focusJointName];
    if (!joint) return;

    // Compute robot bounding box once (or recompute if robot changed)
    if (!bboxRef.current || bboxRobotId.current !== robotRef.current) {
      bboxRobotId.current = robotRef.current;
      const box = new THREE.Box3().setFromObject(robotRef.current);
      bboxRef.current = {
        center: box.getCenter(new THREE.Vector3()),
        radius: box.getSize(new THREE.Vector3()).length() / 2,
      };
    }
    const { center: robotCenter, radius: robotRadius } = bboxRef.current;

    // Joint world position (Y-up, parent group handles Z-up conversion)
    const worldPos = new THREE.Vector3();
    joint.getWorldPosition(worldPos);

    // Joint axis in world space
    const axisLocal = new THREE.Vector3(0, 0, 1);
    if ((joint as any).axis) {
      axisLocal.copy((joint as any).axis as THREE.Vector3);
    }
    const worldQuat = new THREE.Quaternion();
    joint.getWorldQuaternion(worldQuat);
    const axisWorld = axisLocal.clone().applyQuaternion(worldQuat).normalize();

    // "Outward" = direction from robot center to this joint.
    // This naturally points left for left-side joints, right for right-side, etc.
    const outward = new THREE.Vector3().subVectors(worldPos, robotCenter);
    const outwardLen = outward.length();
    if (outwardLen > 0.001) {
      outward.normalize();
    } else {
      // Joint is at the robot's center — default to looking from +Z (front)
      outward.set(0, 0, 1);
    }

    // Pick the best camera direction:
    // - If the joint axis has a meaningful outward component, look along
    //   the axis from the outside (jet engine view)
    // - If the axis is tangent (perpendicular to outward), the axis view
    //   would put us inside the robot. Use outward direction instead,
    //   blended with a touch of axis for depth.
    const axisDot = axisWorld.dot(outward);
    let viewDir: THREE.Vector3;

    if (Math.abs(axisDot) > 0.3) {
      // Axis points meaningfully outward — use axis direction, pick the
      // side that faces away from the robot center
      viewDir = axisDot > 0 ? axisWorld.clone() : axisWorld.clone().negate();
    } else {
      // Axis is tangent to the surface — looking along it would clip through
      // the body. Use outward direction with a slight axis blend so the
      // view isn't perfectly perpendicular to the rotation plane.
      viewDir = outward.clone()
        .addScaledVector(axisWorld, axisDot > 0 ? 0.25 : -0.25)
        .normalize();
    }

    // Slight upward bias so we're not at ground level
    viewDir.y += 0.12;
    viewDir.normalize();

    // Distance scales with robot size so we always clear the geometry
    const distance = Math.max(robotRadius * 0.7, 0.6);
    camGoal.copy(worldPos).addScaledVector(viewDir, distance);

    targetPos.copy(worldPos);

    // When a panel is open on the right, shift the focus point left in screen
    // space so the joint appears centered in the remaining viewport area.
    // Extract the camera's world-space right vector from its matrix, then
    // shift both target and camera position to the left.
    if (panelOpenRight) {
      const right = new THREE.Vector3();
      right.setFromMatrixColumn(camera.matrixWorld, 0); // column 0 = right
      const shiftAmount = distance * 0.35;
      targetPos.addScaledVector(right, -shiftAmount);
      camGoal.addScaledVector(right, -shiftAmount);
    }

    controlsRef.current.target.lerp(targetPos, 0.08);
    camera.position.lerp(camGoal, 0.04);
    controlsRef.current.update();
  });

  return null;
}

function ZoomLimiter({
  robotRef,
  controlsRef,
}: {
  robotRef: MutableRefObject<URDFRobot | null>;
  controlsRef: MutableRefObject<any>;
}) {
  const appliedRef = useRef(false);

  useFrame(() => {
    if (appliedRef.current || !robotRef.current || !controlsRef.current) return;

    // Wait until robot has renderable geometry
    let hasMesh = false;
    robotRef.current.traverse((child) => {
      if ((child as THREE.Mesh).isMesh) hasMesh = true;
    });
    if (!hasMesh) return;

    const box = new THREE.Box3().setFromObject(robotRef.current);
    const radius = box.getSize(new THREE.Vector3()).length() / 2;

    if (radius > 0.001) {
      controlsRef.current.maxDistance = radius * 8;
      controlsRef.current.minDistance = radius * 0.3;
      appliedRef.current = true;
    }
  });

  return null;
}

function ScreenshotCapture({
  robotRef,
  onCaptureFnReady,
}: {
  robotRef: MutableRefObject<URDFRobot | null>;
  onCaptureFnReady: (fn: () => Promise<Blob | null>) => void;
}) {
  const { gl, scene, camera } = useThree();

  const capture = useCallback(async (): Promise<Blob | null> => {
    if (!robotRef.current) return null;

    // Compute bounding box of the robot
    const box = new THREE.Box3().setFromObject(robotRef.current);
    const center = box.getCenter(new THREE.Vector3());
    const size = box.getSize(new THREE.Vector3());
    const radius = size.length() / 2;

    // Save current camera state
    const savedPos = camera.position.clone();
    const savedTarget = new THREE.Vector3(0, 0, -1).applyQuaternion(camera.quaternion).add(camera.position);

    // 3/4 view angle: position at (1.2, 0.8, 1.2) normalized * radius * 2.5
    const dir = new THREE.Vector3(1.2, 0.8, 1.2).normalize();
    camera.position.copy(center).addScaledVector(dir, radius * 2.5);
    camera.lookAt(center);
    camera.updateMatrixWorld();

    // Save current background and set to transparent
    const savedBackground = scene.background;
    scene.background = null;

    // Render one frame
    gl.render(scene, camera);

    // Capture to blob
    const blob = await new Promise<Blob | null>((resolve) => {
      gl.domElement.toBlob((b) => resolve(b), 'image/png');
    });

    // Restore camera and background
    scene.background = savedBackground;
    camera.position.copy(savedPos);
    camera.lookAt(savedTarget);
    camera.updateMatrixWorld();

    // Re-render with original state
    gl.render(scene, camera);

    return blob;
  }, [gl, scene, camera, robotRef]);

  useEffect(() => {
    onCaptureFnReady(capture);
  }, [capture, onCaptureFnReady]);

  return null;
}

const RobotViewer3D = forwardRef<RobotViewer3DHandle, RobotViewer3DProps>(({
  urdfContent,
  meshBlobUrls,
  selectedJoint,
  onJointClick,
  jointValues,
  animatingJoint,
  focusJointName,
  fullScreen,
  panelOpenRight,
  onMeshesLoaded,
}, ref) => {
  const robotRef = useRef<URDFRobot | null>(null);
  const controlsRef = useRef<any>(null);
  const captureFnRef = useRef<(() => Promise<Blob | null>) | null>(null);

  useImperativeHandle(ref, () => ({
    captureScreenshot: async () => {
      if (captureFnRef.current) {
        return captureFnRef.current();
      }
      return null;
    },
  }));

  const handleCaptureFnReady = useCallback((fn: () => Promise<Blob | null>) => {
    captureFnRef.current = fn;
  }, []);

  if (!urdfContent) {
    return (
      <Box
        sx={{
          height: '100%',
          minHeight: 400,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#0a0a0a',
          border: fullScreen ? 'none' : '1px solid rgba(255, 255, 255, 0.12)',
          borderRadius: fullScreen ? 0 : 1,
        }}
      >
        <Typography color="text.secondary">
          3D preview available after conversion
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: '100%',
        minHeight: fullScreen ? undefined : 400,
        backgroundColor: '#0a0a0a',
        border: fullScreen ? 'none' : '1px solid rgba(255, 255, 255, 0.12)',
        borderRadius: fullScreen ? 0 : 1,
        overflow: 'hidden',
      }}
    >
      <Canvas
        camera={{ position: [2, 2, 2], fov: 50 }}
        gl={{ preserveDrawingBuffer: true, alpha: true }}
      >
        <color attach="background" args={['#0a0a0a']} />
        <ambientLight intensity={0.4} />
        <directionalLight position={[5, 5, 5]} intensity={0.8} />
        <Suspense fallback={null}>
          <URDFModel
            urdfContent={urdfContent}
            meshBlobUrls={meshBlobUrls}
            selectedJoint={selectedJoint}
            jointValues={jointValues}
            robotRef={robotRef}
            onMeshesLoaded={onMeshesLoaded}
          />
        </Suspense>
        <JointHighlighter robotRef={robotRef} selectedJoint={selectedJoint} />
        {animatingJoint && (
          <JointAnimator robotRef={robotRef} animatingJoint={animatingJoint} />
        )}
        {focusJointName && (
          <CameraFocuser
            robotRef={robotRef}
            focusJointName={focusJointName}
            controlsRef={controlsRef}
            panelOpenRight={panelOpenRight}
          />
        )}
        <ScreenshotCapture robotRef={robotRef} onCaptureFnReady={handleCaptureFnReady} />
        <ZoomLimiter robotRef={robotRef} controlsRef={controlsRef} />
        <OrbitControls ref={controlsRef} enableDamping />
      </Canvas>
    </Box>
  );
});

export default RobotViewer3D;
